import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { ToastService } from 'src/app/core/services/toast.service';
import {
  EnvironmentInjector,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import { readLicenseError } from 'src/app/core/errors/license-error';

/** Token para ignorar toast de erro em requisições específicas */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

export const toastInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(EnvironmentInjector);
  const skipHeader = req.headers.get('X-Skip-Toast') === '1';
  const skipContext = req.context.get(SKIP_ERROR_TOAST);

  return next(req).pipe(
    catchError((err: unknown) => {
      // Ignora toast se marcado para skip
      if (skipHeader || skipContext) {
        return throwError(() => err);
      }

      if (err instanceof HttpErrorResponse) {
        runInInjectionContext(injector, () => {
          const t = inject(TranslateService);
          const messageService = inject(ToastService);

          // Status 0 → erro de rede ou cancelamento
          if (err.status === 0) {
            const isAbort = (err.error as any)?.name === 'AbortError';
            if (!isAbort) {
              messageService.add({
                severity: 'error',
                summary: t.instant('shared.errors.errorTitle'),
                detail: t.instant('shared.errors.network'),
                life: 5000,
              });
            }
            return;
          }

          // Status 401 → não autorizado
          if (err.status === 401) {
            messageService.add({
              severity: 'error',
              summary: t.instant('shared.errors.errorTitle'),
              detail: t.instant('features.errors.accessDenied.toast'),
              life: 5000,
            });
            return;
          }

          // Falta de LICENÇA, que o backend marca com um código próprio dentro do 403 (AE-711).
          // Vem antes do 403 genérico de propósito: sem esta checagem os dois casos voltariam a
          // sair com a mensagem de permissão, que é o problema que a task veio resolver.
          const licenseError = readLicenseError(err);

          if (licenseError === 'package') {
            // Sem o pacote raiz nada funciona, e um toast por requisição recusada empilharia a
            // tela inteira. Quem trata é o `license.interceptor`, levando o usuário para a tela
            // de licença.
            return;
          }

          if (licenseError === 'feature') {
            // Título próprio, e não o "Erro" compartilhado: falta de licença não é falha, é limite
            // do plano. Com o ícone de aviso ao lado, "Erro" ainda mandava o usuário procurar
            // suporte quando o caminho é o consultor comercial.
            messageService.add({
              severity: 'warn',
              summary: t.instant('features.errors.licenseRequired.toastTitle'),
              detail: t.instant('features.errors.licenseRequired.featureToast'),
              life: 5000,
            });
            return;
          }

          // Status 403 → proibido
          if (err.status === 403) {
            messageService.add({
              severity: 'error',
              summary: t.instant('shared.errors.errorTitle'),
              detail: t.instant('shared.errors.forbidden'),
              life: 5000,
            });
            return;
          }

          // Status 404 → não encontrado.
          // Silencioso por padrão: a maioria dos 404 é ausência esperada, tratada pelo componente.
          // Quando o backend manda um ProblemDetails com título, ele descreve algo que o usuário
          // pediu e não existe — vale o toast.
          if (err.status === 404) {
            const problem = err.error as { title?: string } | null;
            if (problem?.title) {
              messageService.add({
                severity: 'warn',
                summary: t.instant('shared.errors.errorTitle'),
                detail: problem.title,
                life: 5000,
              });
            }
            return;
          }

          // Status 500+ → erro interno do servidor.
          // Exceção: 502/503/504 são recusas deliberadas, e o título do ProblemDetails traz
          // orientação de quem recusou ("tente filtrar por uma coleção específica", no timeout do
          // relatório de sucesso). O 500 fica de fora porque lá o título é sempre "Erro interno",
          // posto pelo ExceptionHandler justamente para não vazar detalhe de exceção — mostrar
          // aquilo seria pior que a mensagem traduzida.
          if (err.status >= 500) {
            const problem = err.error as { title?: string } | null;
            const orientacaoDoServidor =
              err.status >= 502 && err.status <= 504 ? problem?.title : null;

            messageService.add({
              severity: 'error',
              summary: t.instant('shared.errors.errorTitle'),
              detail: orientacaoDoServidor ?? t.instant('shared.errors.server'),
              life: 5000,
            });
            return;
          }

          // Outros erros → mensagem do servidor, quando houver.
          // A API responde ProblemDetails (RFC 9457): a mensagem útil está em `title`; `detail` é
          // um texto genérico. `message` nunca existiu nessa resposta — a leitura anterior caía
          // sempre no fallback.
          const problem = err.error as { title?: string } | null;
          const serverMessage = problem?.title;
          messageService.add({
            severity: 'error',
            summary: t.instant('shared.errors.errorTitle'),
            detail: serverMessage ?? t.instant('shared.errors.generic'),
            life: 5000,
          });
        });
      } else {
        // Erro não HTTP
        runInInjectionContext(injector, () => {
          const t = inject(TranslateService);
          const messageService = inject(ToastService);
          messageService.add({
            severity: 'error',
            summary: t.instant('shared.errors.errorTitle'),
            detail: t.instant('shared.errors.unexpected'),
            life: 5000,
          });
        });
      }

      return throwError(() => err);
    }),
  );
};
